// frontend/src/store/authStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authAPI from '../services/auth.api.js';
import useWalletStore from './walletStore.js';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isInitialized: false,
      userStats: null,

      // Action: Load user stats
      loadUserStats: async () => {
        try {
          const stats = {
            postsCount: 0,
            followersCount: 0,
            followingCount: 0
          };

          set({ userStats: stats });
          return stats;
        } catch (error) {
          console.error('Failed to load user stats:', error);
          set({
            userStats: {
              postsCount: 0,
              followersCount: 0,
              followingCount: 0
            }
          });
          return null;
        }
      },

      // ✅ NEW: Sync user with wallet store
      syncUserWithWallet: (user) => {
        if (user) {
          useWalletStore.getState().setUser(user);
        }
      },

      // Action: Initialize app (check for existing auth)
      initialize: async () => {
        // Check localStorage first (remember me), then sessionStorage
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

        if (!token) {
          set({ isInitialized: true });
          return;
        }

        try {
          // Determine which storage to use
          const storage = localStorage.getItem('accessToken') ? localStorage : sessionStorage;
          const user = JSON.parse(storage.getItem('user') || 'null');

          if (user) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false
            });

            // ✅ Sync user with wallet store
            get().syncUserWithWallet(user);

            // Load user stats after authentication
            setTimeout(() => {
              get().loadUserStats();
            }, 1000);
          }
        } catch (error) {
          console.error('Initialization error:', error);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('refreshToken');
          sessionStorage.removeItem('user');
        } finally {
          set({ isInitialized: true });
        }
      },

      // Action: Register
      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.register(userData);

          // Return the response so the component can navigate
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Login
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.login(credentials);
          const { user, accessToken, refreshToken } = response.data;

          // Determine storage based on rememberMe
          const storage = credentials.rememberMe ? localStorage : sessionStorage;

          // Store tokens and user in the appropriate storage
          storage.setItem('accessToken', accessToken);
          storage.setItem('refreshToken', refreshToken);
          storage.setItem('user', JSON.stringify(user));

          // If rememberMe is true, also store email for auto-fill
          if (credentials.rememberMe) {
            localStorage.setItem('savedEmail', credentials.email);
          } else {
            localStorage.removeItem('savedEmail');
          }

          // Clear from other storage to avoid conflicts
          const otherStorage = credentials.rememberMe ? sessionStorage : localStorage;
          otherStorage.removeItem('accessToken');
          otherStorage.removeItem('refreshToken');
          otherStorage.removeItem('user');

          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            error: null,
          });

          // Sync user with wallet store
          get().syncUserWithWallet(user);

          // Load user stats after successful login
          get().loadUserStats();

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Login failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Verify Phone
      verifyPhone: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.verifyPhone(data);

          // Update user's phone verification status
          const currentUser = get().user;
          if (currentUser) {
            const updatedUser = { ...currentUser, phoneVerified: true };
            set({ user: updatedUser });
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // ✅ Sync updated user with wallet store
            get().syncUserWithWallet(updatedUser);
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Verification failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Upload Avatar
      uploadAvatar: async (file) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.uploadAvatar(file);
          const { user } = response.data;

          set({
            user,
            isLoading: false
          });

          localStorage.setItem('user', JSON.stringify(user));

          // ✅ Sync updated user with wallet store
          get().syncUserWithWallet(user);

          // Refresh user stats
          get().loadUserStats();

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Upload failed';

          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Action: Delete Avatar
      deleteAvatar: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.deleteAvatar();
          const { user } = response.data;

          set({
            user,
            isLoading: false
          });

          localStorage.setItem('user', JSON.stringify(user));

          // ✅ Sync updated user with wallet store
          get().syncUserWithWallet(user);

          // Refresh user stats
          get().loadUserStats();

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Delete failed';

          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // Action: Logout
      logout: async () => {
        try {
          await authAPI.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            error: null,
            userStats: null,
          });

          // ✅ Clear user from wallet store
          useWalletStore.getState().setUser(null);

          // Clear both storages
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('refreshToken');
          sessionStorage.removeItem('user');
          localStorage.removeItem('auth-storage');
        }
      },

      // Action: Forgot Password
      forgotPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.forgotPassword(email);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Request failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Verify Reset OTP
      verifyResetOtp: async (email, otp) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.verifyResetOtp(email, otp);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Verification failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Resend Reset OTP
      resendResetOtp: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.resendResetOtp(email);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to resend OTP';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Reset Password
      resetPassword: async (token, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.resetPassword(token, newPassword);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Reset failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get Profile
      getProfile: async () => {
        try {
          const response = await authAPI.getProfile();
          const updatedUser = response.data;
          set({ user: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));

          // ✅ Sync updated user with wallet store
          get().syncUserWithWallet(updatedUser);

          // Also load user stats when getting profile
          get().loadUserStats();

          return updatedUser;
        } catch (error) {
          // If 401, the axios interceptor will handle it
          if (error.response?.status === 401) {
            console.log('Authentication expired, token refresh will be attempted');
          } else {
            console.error('Get profile error:', error);
          }
          return null;
        }
      },

      // Action: Update Profile
      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.updateProfile(profileData);
          const updatedUser = response.data;
          set({ user: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));

          // ✅ Sync updated user with wallet store
          get().syncUserWithWallet(updatedUser);

          // Refresh user stats after profile update
          get().loadUserStats();

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Update failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Change Password
      changePassword: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authAPI.changePassword(data);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Change failed';

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // ✅ NEW: Set user directly (for manual updates)
      setUser: (user) => {
        set({ user });
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
          get().syncUserWithWallet(user);
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        userStats: state.userStats,
      }),
    }
  )
);

export default useAuthStore;